import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { esNotificationController } from '@examsoft/haste-ui';
import { TranslationService } from '@examsoft/translation';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { maxBy } from 'lodash';
import { tap } from 'rxjs/operators';
import { go } from 'src/app/lib/helpers';
import { AppState } from 'src/app/shared/state/app.state';
import { PaymentService } from '../payment.service';
import {
  GetPaymentWindowsResponseItem,
  GetStudentPaymentHistoryResponseItem,
  PayType,
} from '../payment.types';
import {
  GetPaymentType,
  GetPaymentWindows,
  GetStudentPaymentHistory,
  PayPalPaymentSubmitted,
  SubmitVoucherPayment,
  ValidateStudentPayment,
} from './payment.actions';

// Nulls represent the unknown case on first load
export interface PaymentStateModel {
  hasStudentPaid: boolean | null;
  paymentType: PayType | null;
  paymentWindows: GetPaymentWindowsResponseItem[];
  studentPaymentHistory: GetStudentPaymentHistoryResponseItem[];
}

export type PaymentStateContext = StateContext<PaymentStateModel>;

@State<PaymentStateModel>({
  name: 'Payment',
  defaults: {
    hasStudentPaid: null,
    paymentType: null,
    paymentWindows: [],
    studentPaymentHistory: [],
  },
})
@Injectable()
export class PaymentState {
  constructor(
    private paymentService: PaymentService,
    private store: Store,
    private translateService: TranslationService
  ) {}

  @Selector([PaymentState])
  static hasStudentPaid(state: PaymentStateModel) {
    return state.hasStudentPaid;
  }

  @Selector([PaymentState])
  static selectPaymentType(state: PaymentStateModel) {
    return state.paymentType;
  }

  @Selector([PaymentState])
  static selectPaymentWindows(state: PaymentStateModel) {
    return state.paymentWindows;
  }

  @Selector([PaymentState])
  static selectStudentPayPeriods(state: PaymentStateModel) {
    return state.studentPaymentHistory;
  }

  @Selector([PaymentState.selectStudentPayPeriods])
  static selectUniqueStudentPayPeriodsByCreatedDate(
    studentPaymentHistory: ReturnType<typeof PaymentState.selectStudentPayPeriods>
  ) {
    // Group by paymentWindow
    const grouped: Record<string, GetStudentPaymentHistoryResponseItem[]> = {};
    for (const item of studentPaymentHistory) {
      const id = item.payWindow.id;

      if (grouped[id]) {
        grouped[id].push(item);
      } else {
        grouped[id] = [item];
      }
    }

    // Return max by created date including only paid periods
    return Object.values(grouped)
      .map((value) => maxBy(value, (v) => new Date(v.createdDate).getTime()))
      .filter((v) => {
        return v.paid && Date.now() < new Date(v.payWindow.from).getTime();
      });
  }

  @Selector([PaymentState.selectPaymentWindows, PaymentState.selectPaymentType])
  static selectUpcomingPaymentWindowsByPaymentType(
    paymentWindows: ReturnType<typeof PaymentState.selectPaymentWindows>,
    paymentType: ReturnType<typeof PaymentState.selectPaymentType>
  ) {
    const filterDate = (payment: GetPaymentWindowsResponseItem) => {
      const endDate = formatDate(payment.to, 'shortDate', 'en-US', 'UTC');
      return payment.amount > 0 && Date.now() < new Date(endDate).getTime();
    };
    const getDateRangeLabel = (payment: GetPaymentWindowsResponseItem) => {
      const startDate = formatDate(payment.from, 'mediumDate', 'en-US', 'UTC');
      const endDate = formatDate(payment.to, 'mediumDate', 'en-US', 'UTC');
      return `${startDate} - ${endDate}`;
    };
    if (paymentType === 'paypal') {
      return paymentWindows
        .filter((payment) => payment.amount > 0 && filterDate(payment))
        .map((paymentWindow) => {
          const label = `${getDateRangeLabel(paymentWindow)}: ${paymentWindow.amount}${
            paymentWindow.currencyCode
          }`;

          return {
            ...paymentWindow,
            label,
          };
        });
    }

    return paymentWindows
      .filter((payment) => payment.generatedVouchersCount > 0 && filterDate(payment))
      .map((paymentWindow) => {
        const label = getDateRangeLabel(paymentWindow);
        return {
          ...paymentWindow,
          label,
        };
      });
  }

  @Action(ValidateStudentPayment)
  ValidateStudentPayment(ctx: PaymentStateContext) {
    return this.paymentService.validateStudentPayment().pipe(
      tap((res) => {
        ctx.patchState({
          hasStudentPaid: res,
        });
      })
    );
  }

  @Action(GetPaymentType)
  GetPaymentType(ctx: PaymentStateContext) {
    const institution = this.store.selectSnapshot(AppState.institution);

    return this.paymentService.getPaymentType(institution.id).pipe(
      tap((res) => {
        ctx.patchState({
          paymentType: res.payType,
        });
      })
    );
  }

  @Action(GetPaymentWindows)
  GetPaymentWindows(ctx: PaymentStateContext) {
    const institution = this.store.selectSnapshot(AppState.institution);

    return this.paymentService.getPaymentWindows(institution.id).pipe(
      tap((res) => {
        ctx.patchState({
          paymentWindows: res,
        });
      })
    );
  }

  getVoucherErrorMessage(errorMessage: string) {
    switch (errorMessage) {
      case 'ALREADY_CLAIMED':
        return this.translateService.get('Code already in use, please try again.');

      case 'UNKNOWN_VOUCHER':
        return this.translateService.get('Invalid code, please try again');

      case 'VOUCHER_CODE_INVALID':
        return this.translateService.get('Invalid code, please try again');

      default:
        return this.translateService.get(
          'There was an error claiming your code. Please try again.'
        );
    }
  }

  @Action(GetStudentPaymentHistory)
  getStudentPaymentHistory(ctx: PaymentStateContext, action: GetStudentPaymentHistory) {
    const institution = this.store.selectSnapshot(AppState.institution);
    const student = this.store.selectSnapshot(AppState.selectUser);

    return this.paymentService.getStudentPaymentWindows(institution.id, student.id).pipe(
      tap((res) =>
        ctx.patchState({
          studentPaymentHistory: res,
        })
      )
    );
  }

  @Action(SubmitVoucherPayment)
  async SubmitVoucherPayment(ctx: PaymentStateContext, action: SubmitVoucherPayment) {
    const { code } = action;
    const { id } = this.store.selectSnapshot(AppState.selectUser);

    const [, claimVoucherErr] = await go(this.paymentService.claimVoucher(id, code).toPromise());

    if (claimVoucherErr) {
      esNotificationController.create({
        position: 'top-right',
        duration: 10000,
        state: 'danger',
        messageText: this.translateService.get(this.getVoucherErrorMessage(claimVoucherErr.error)),
      });

      throw claimVoucherErr;
    }

    const [voucher, getVoucherErr] = await go(
      this.paymentService.getVoucherByCode(code).toPromise()
    );

    if (getVoucherErr) {
      esNotificationController.create({
        position: 'top-right',
        duration: 10000,
        state: 'danger',
        messageText: this.translateService.get('Error fetching claimed voucher'),
      });

      throw getVoucherErr;
    }

    esNotificationController.create({
      position: 'top-right',
      duration: 10000,
      state: 'success',
      messageText: this.translateService.get(
        'Your voucher has been claimed successfully. It will be valid from {{startDate}} to {{endDate}}.',
        {
          startDate: formatDate(voucher.payWindow.from, 'mediumDate', 'en-US', 'UTC'),
          endDate: formatDate(voucher.payWindow.to, 'mediumDate', 'en-US', 'UTC'),
        }
      ),
    });

    return ctx.dispatch([new ValidateStudentPayment(), new GetStudentPaymentHistory()]);
  }

  @Action(PayPalPaymentSubmitted)
  async PayPalPaymentSubmitted(ctx: PaymentStateContext, action: PayPalPaymentSubmitted) {
    const user = this.store.selectSnapshot(AppState.selectUser);
    const institution = this.store.selectSnapshot(AppState.institution);

    const payment = {
      description: `Examplify ${institution.name}`,
      institutionId: institution.id,
      studentId: user.id,
      payWindowId: action.paymentWindow.id,
      paytype: 'paypal',
      transactionId: institution.id + '-' + user.id + '-' + action.paymentData.orderID,
    };

    const [, postPaymentErr] = await go(this.paymentService.postPayment(payment).toPromise());

    if (postPaymentErr) {
      esNotificationController.create({
        position: 'top-right',
        duration: 10000,
        state: 'danger',
        messageText: this.translateService.get(
          'There was an issue notifying ExamSoft of payment. Please contact us to resolve this issue'
        ),
      });
      throw postPaymentErr;
    }

    esNotificationController.create({
      position: 'top-right',
      duration: 10000,
      messageText: this.translateService.get(
        'You have successfully paid for the period starting on {{startDate}} and ending on {{endDate}}',
        {
          startDate: formatDate(action.paymentWindow.from, 'mediumDate', 'en-US', 'UTC'),
          endDate: formatDate(action.paymentWindow.to, 'mediumDate', 'en-US', 'UTC'),
        }
      ),
    });

    return ctx.dispatch([new ValidateStudentPayment(), new GetStudentPaymentHistory()]);
  }
}
